import { useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard,
  Ticket,
  PlusCircle,
  BookOpen,

  Users,
  Building2,
  Timer,
  Key,
  ShieldCheck,
  FileText,
  LogOut,
  Shield,
  ExternalLink,
  FileCode2,
  BookMarked,
  Mail,
  Inbox,
  GitBranch,
  MapPin,
  Cable,
  BarChart3,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { APP_VERSION } from '@uniticket/shared';
import { ChangelogDialog } from '@/components/custom/ChangelogDialog';

interface SidebarProps {
  onNavigate?: () => void;
}

interface NavItemDef {
  to?: string;
  href?: string;
  labelKey: string;
  icon: React.ComponentType<{ className?: string }>;
  disabled?: boolean;
  soon?: boolean;
  /**
   * Optional override for the active-highlight decision. When set, its result
   * replaces react-router's default (descendant-prefix) match. Used so the
   * "Tickets" item does not also light up on the "/tickets/new" create subtree,
   * which is owned by the separate "Create Ticket" item.
   */
  activeMatch?: (pathname: string) => boolean;
}

// "/tickets" is a prefix of "/tickets/new", so NavLink's default descendant match
// would highlight BOTH items on the create pages. Keep Tickets active for the list
// and ticket-detail pages, but yield the "/tickets/new" subtree to Create Ticket.
const isTicketsSectionActive = (p: string): boolean =>
  (p === '/tickets' || p.startsWith('/tickets/')) &&
  !(p === '/tickets/new' || p.startsWith('/tickets/new/'));

const mainNavItems: NavItemDef[] = [
  { to: '/dashboard', labelKey: 'nav.dashboard', icon: LayoutDashboard },
  { to: '/tickets', labelKey: 'nav.tickets', icon: Ticket, activeMatch: isTicketsSectionActive },
  { to: '/tickets/new', labelKey: 'nav.createTicket', icon: PlusCircle },
  { to: '/address-information', labelKey: 'nav.addressInformation', icon: MapPin },
  { to: '/help', labelKey: 'nav.userGuide', icon: BookOpen },
];

const reportingNavItems: NavItemDef[] = [
  { to: '/reporting', labelKey: 'nav.reporting', icon: BarChart3 },
];

const adminNavItems: NavItemDef[] = [
  { to: '/admin/users', labelKey: 'nav.userManagement', icon: Users },
  { to: '/admin/operators', labelKey: 'nav.operatorSettings', icon: Building2 },
  { to: '/admin/groups', labelKey: 'nav.groupManagement', icon: Shield },
  { to: '/admin/sla-configs', labelKey: 'nav.slaConfiguration', icon: Timer },
  { to: '/admin/api-keys', labelKey: 'nav.apiKeys', icon: Key },
  { to: '/admin/audit-log', labelKey: 'nav.auditLog', icon: FileText },
  { to: '/admin/email-templates', labelKey: 'nav.emailTemplates', icon: Mail },
  { to: '/admin/email-queue', labelKey: 'nav.emailQueue', icon: Inbox },
  { to: '/admin/assignment-routing', labelKey: 'nav.assignmentRouting', icon: GitBranch },
  { to: '/admin/ordering-config', labelKey: 'nav.orderingConfig', icon: Cable },
  { to: '/admin/privacy', labelKey: 'nav.privacyRequests', icon: ShieldCheck },
];

const adminExternalItems: NavItemDef[] = [
  { href: '/api/docs', labelKey: 'nav.apiDocumentation', icon: FileCode2 },
  { href: '/api/guide', labelKey: 'nav.integrationGuide', icon: BookMarked },
];

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export function Sidebar({ onNavigate }: SidebarProps) {
  const { user, isAdmin, isInternal, isExternal, logout } = useAuth();
  const [logoError, setLogoError] = useState(false);
  const [changelogOpen, setChangelogOpen] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation('common');

  async function handleSignOut() {
    await logout();
    navigate('/login');
    onNavigate?.();
  }

  return (
    <aside className="flex flex-col w-64 h-full bg-white border-r border-slate-200">
      {/* Logo */}
      <div className="flex items-center gap-3 h-16 px-5 border-b border-slate-100">
        <div className="w-8 h-8 rounded-lg bg-brand-gradient flex items-center justify-center">
          <Ticket className="w-4 h-4 text-white" />
        </div>
        <span className="text-lg font-bold gradient-text">UniTicket</span>
      </div>

      {/* Operator logo (external users only) */}
      {isExternal && user && (
        <div className="px-5 py-3 border-b border-slate-100">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2">
            {t('nav.yourOperator')}
          </p>
          {!logoError ? (
            <img
              src={`/logos/${user.operatorCode}.png`}
              alt={user.operatorName}
              className="mx-auto max-w-[180px] max-h-[48px] object-contain"
              onError={() => setLogoError(true)}
            />
          ) : (
            <div className="flex items-center justify-center h-12 rounded-lg bg-slate-50 border border-slate-100">
              <span className="text-sm font-semibold text-slate-600">
                {user.operatorName}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto custom-scrollbar py-4">
        {/* Main section */}
        <div className="px-5 mb-2">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
            {t('nav.main')}
          </span>
        </div>
        <div className="space-y-0.5 px-2">
          {mainNavItems.map((item) => (
            <NavItem key={item.labelKey} to={item.to!} label={t(item.labelKey)} icon={item.icon} onNavigate={onNavigate} activeMatch={item.activeMatch} />
          ))}
        </div>

        {/* Reporting section (internal Unifiber staff only) */}
        {isInternal && (
          <>
            <div className="px-5 mt-6 mb-2">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                {t('nav.reportingSection')}
              </span>
            </div>
            <div className="space-y-0.5 px-2">
              {reportingNavItems.map((item) => (
                <NavItem key={item.labelKey} to={item.to!} label={t(item.labelKey)} icon={item.icon} onNavigate={onNavigate} />
              ))}
            </div>
          </>
        )}

        {/* Administration section (admin only) */}
        {isAdmin && (
          <>
            <div className="px-5 mt-6 mb-2">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                {t('nav.administration')}
              </span>
            </div>
            <div className="space-y-0.5 px-2">
              {adminNavItems.map((item) => (
                <NavItem key={item.labelKey} to={item.to!} label={t(item.labelKey)} icon={item.icon} onNavigate={onNavigate} />
              ))}
              <div className="my-2 mx-3 border-t border-slate-100" />
              {adminExternalItems.map((item) => (
                <ExternalNavItem key={item.labelKey} href={item.href!} label={t(item.labelKey)} icon={item.icon} />
              ))}
            </div>
          </>
        )}
      </nav>

      {/* User section */}
      <div className="border-t border-slate-100 p-4">
        <div className="flex items-center gap-3 w-full">
          <Avatar className="h-9 w-9 shrink-0">
            <AvatarFallback className="text-[11px]">
              {user ? getInitials(user.displayName) : '?'}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-900 truncate">{user?.displayName ?? ''}</p>
            <p className="text-xs text-slate-400 truncate">{user?.email ?? ''}</p>
          </div>
          <button
            type="button"
            onClick={handleSignOut}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors shrink-0"
            title={t('topbar.signOut')}
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
        <button
          type="button"
          onClick={() => setChangelogOpen(true)}
          aria-label={t('changelog.openAria', { version: APP_VERSION })}
          className="mt-2 w-full text-center text-[10px] text-slate-300 transition-colors hover:text-slate-100"
        >
          UniTicket v{APP_VERSION}
        </button>
      </div>
      <ChangelogDialog open={changelogOpen} onOpenChange={setChangelogOpen} />
    </aside>
  );
}

interface NavItemProps {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  disabled?: boolean;
  soon?: boolean;
  onNavigate?: () => void;
  activeMatch?: (pathname: string) => boolean;
}

function NavItem({ to, label, icon: Icon, disabled, soon, onNavigate, activeMatch }: NavItemProps) {
  const { t } = useTranslation('common');
  const location = useLocation();

  if (disabled) {
    return (
      <span className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-slate-400 cursor-default opacity-50">
        <Icon className="w-5 h-5" />
        <span className="flex-1">{label}</span>
        {soon && (
          <span className="text-[10px] font-bold px-1.5 py-0.5 bg-slate-100 text-slate-400 rounded">
            {t('badges.soon')}
          </span>
        )}
      </span>
    );
  }

  return (
    <NavLink
      to={to}
      onClick={onNavigate}
      className={({ isActive }) => {
        const active = activeMatch ? activeMatch(location.pathname) : isActive;
        return cn(
          'flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200',
          active
            ? 'text-brand-rose bg-gradient-to-r from-brand-rose/[0.08] to-transparent border-r-[3px] border-brand-rose'
            : 'text-slate-600 hover:bg-black/[0.03]'
        );
      }}
    >
      <Icon className="w-5 h-5" />
      <span>{label}</span>
    </NavLink>
  );
}

interface ExternalNavItemProps {
  href: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

function ExternalNavItem({ href, label, icon: Icon }: ExternalNavItemProps) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 text-slate-600 hover:bg-black/[0.03]"
    >
      <Icon className="w-5 h-5" />
      <span className="flex-1">{label}</span>
      <ExternalLink className="w-3 h-3 text-slate-400" />
    </a>
  );
}
